import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { AppRouterService } from 'app/services/app/router.service';
import { AppService } from '@app/services/app/app.service';
import { VersionsViewService } from '@app/services/app/versions.service';

import * as Objects from '@core/objects';
// core recorder state 
import * as fromState from '@core/store/state/recorder/recorder.state';
import { RecorderService } from '@core/services/recorder/recorder.service';

import { VersionFeedback, FeedbackStatusCodeEnum } from '@core/models/version-feedback';
import { VersionShare, VersionShareType } from '@core/models/version-share.model';
import { VersionShareFeedbackMessage, VersionShareFeedbackMessageType } from '@core/models/version-share-message.model';
import { VersionMessageContext } from '@core/constants/version-message-context.constants';
import { InterfaceListenerService } from '@core/services/interface-listener/interface-listener.service';

import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';
import { VersionListItemMenuClick } from '@core/components/versions/list/version-list-item-menu-click';

@Component({
    selector: 'version-details-content-messages-version-share-feedback',
    templateUrl: './messages-version-share-feedback.component.html',
    styleUrls: ['./messages-version-share-feedback.component.scss']
})
export class VersionsDetailsMessagesVersionShareFeedbackComponent implements OnInit, OnDestroy, OnChanges {
    public readonly MESSAGE_SHARE_FEEDBACK_CONTEXT = 'message-share-feedback';
    public readonly VERSION_SHARE_MESSAGE_TYPE = VersionShareFeedbackMessageType;
    // msg available contexts: used to select and filter the messages to display
    public readonly VERSION_MESSAGE_CONTEXT = VersionMessageContext;

    @Input() version: Objects.Version;
    @Input() feedbacks: Array<VersionFeedback>;
    @Input() versionShares: Array<VersionShare> = null;
    @Input() versionShareMessages: Array<VersionShareFeedbackMessage> = null;
    @Input() share: any = null;
    // REMINDER: share.options (are boolean)
    // {allow_see,get_approval,get_comments,get_signature}

    public user: Objects.User = null;
    public uploads: Array<Objects.Version>;
    public messagesList: Array<VersionShareFeedbackMessage> = [];
    public prefixUrl: string = '';
    public prefixDeleteUrl: string = '';
    public messageAddUrl: string = '';
    public context: string = this.MESSAGE_SHARE_FEEDBACK_CONTEXT;
    public msgActionAccess: boolean = false;
    public hash: string = '';

    // public versionShareMessages: Array<VersionShareFeedbackMessage> = null;
    public feedbackTextMessages: Array<Objects.VersionMessage> = null;
    
    protected messagesSubscriptor: Subscription;
    protected appSubscriptor: Subscription;
    protected recorderSubscriptor: Subscription;
    protected iListenerSubs: Subscription;
    protected uploadsSubscriptor: Subscription;

    public menuVersion: Array<ThreeDotsMenuItem> = [{
        title: "See Artwork Details",
        key: "details",
        icon: "see-details"
    }];

    protected userFeedback: VersionFeedback = null;

    constructor(private appRouter: AppRouterService,
        public appSrv: AppService,
        private versionsViewSrv: VersionsViewService,
        private recorderSrv: RecorderService,
        private iListener: InterfaceListenerService 
    ) { }

    ngOnInit() {        
        this.appSubscriptor = this.appSrv.getSession().subscribe(session => {
            this.user = session.current.user;
            this.updateUserApproval();
            this.updateUserAccess();
        });

        this.messagesSubscriptor = this.versionsViewSrv.selectComments().subscribe((messages: Array<Objects.VersionMessage>) => {
            // Here we filter the messages which have context to get the messages given on the feedback (2023/09/08context pattern ==> feedback-{id_feedback})
            if (messages != null){
                this.feedbackTextMessages = messages.filter((m:Objects.VersionMessage) => 
                            m.context.startsWith(this.VERSION_MESSAGE_CONTEXT.FEEDBACK) || 
                            m.context.startsWith(this.VERSION_MESSAGE_CONTEXT.UPLOAD_VERSION) );   
                this.updateMessageList();
            }
        });

        this.uploadsSubscriptor = this.versionsViewSrv.selectUploadedVersionUploads().subscribe((uploads: Objects.Version[]) =>{
            this.uploads = uploads;
        });
        
        // here we check the recorder status to reload messages
        this.recorderSubscriptor = this.recorderSrv.getStatus().subscribe((status: fromState.RecorderStatus ) => {
            if(status == fromState.RecorderStatus.FINISHED_SUCCESS){
                this.versionsViewSrv.loadComments();
            }
        });

        this.iListenerSubs = this.iListener.getVersionFeedbackDone().subscribe((version: Objects.Version) => {
            // Handle the version feedback 'onDone' event here to reload the comments.
            if(this.version != null && version!=null && version.id == this.version.id){
                this.versionsViewSrv.loadComments();
                this.updateUserAccess();
            }
        });

    }

    ngOnDestroy() {
        this.messagesList = null;
        this.appSubscriptor.unsubscribe();
        this.messagesSubscriptor.unsubscribe();
        this.recorderSubscriptor.unsubscribe();
        this.iListenerSubs.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if(changes.feedbacks){ // TODO maybe remove
            this.updateMessageList();
            this.updateUserApproval();
            this.updateUserAccess();

        }

        if(changes.versionShareMessages && this.versionShareMessages != null){
            this.updateMessageList();
        }

        // Here now we allow save message on feedback through /share using hash and context on messagesa and files
        // and only let user add message and notes when feedbacks is still pending
        // set msgActionAccess according share.options, feedback status and user logged to let the river flows
        if(changes.share && this.share != null && this.share.entry != null){

            this.updateUserAccess();
            this.hash = this.share.entry.hash;
            this.messageAddUrl = ("/web/share/p/feedback/"+this.share.entry.hash+"/versions/"+this.version.id+"/messages");
            this.prefixDeleteUrl = ("/web/share/p/feedback/"+this.share.entry.hash);
        } 
        this.prefixUrl = ("/p/feedback/versions/"+this.version.id); // only for get legacy notes not for save

    }

    onVersionMenuClick(menu: VersionListItemMenuClick) {
        // console.debug(" menu original click",menu);
        switch(menu.key) {
            case "details":
                // url to go to Artwork Details
                this.appRouter.navigate( "/versions/"+menu.version.id);
                break;
        }
    }

    onClickVersionPreview(file: Objects.File, version: Objects.Version){
        if (file != null && version != null){
            this.versionsViewSrv.clear();
            this.appRouter.navigate('/versions/'+version.id + "/files/"+file.id);
        }
    }


    onUpdateMessages(message: Objects.Message) {
        this.versionsViewSrv.loadComments();
    }

    onDeleteMessage(message: Objects.Message) {
        this.versionsViewSrv.loadComments();
    }

    protected updateMessageList() : void {
        if(this.feedbackTextMessages != null && this.versionShareMessages != null){
            let msgList: VersionShareFeedbackMessage[]  =  this.feedbackTextMessages.map((msg: Objects.VersionMessage) => {

                let shares = (this.versionShares.length > 0) ? this.versionShares.filter((vs:VersionShare) => vs.entry.user.id == msg.message.user.id) : [];
                return {    
                    uuid: uuidv4(),
                    type: this.VERSION_SHARE_MESSAGE_TYPE.TEXT_MESSAGE_TYPE,
                    message: msg.message, 
                    user_version_shares: shares, 
                    date: msg.message.date,
                    version: this.version
                };
            });

            this.messagesList = [...this.versionShareMessages, ...msgList];
        }
    }

    protected updateUserApproval() : void {
        if(this.feedbacks == null || this.user == null)
            return;
        
        let userFeedbacks = this.feedbacks.filter((f:VersionFeedback) => f.user.id == this.user.id)
        this.userFeedback = userFeedbacks.length > 0 ? userFeedbacks[0] : null;
    }
    // need to check both user and sahre to manage current user access
    protected updateUserAccess() : void {
        if(this.user != null && this.share != null){
            // need to check approval in case only comments were asked
            this.msgActionAccess = (this.share.options.get_comments &&
                                this.share.entry.user.id == this.user.id &&
                                this.userFeedback != null && (
                                    this.userFeedback.status.status_code == FeedbackStatusCodeEnum.OPENED_CODE
                                     )
                            );
        }
        
    }

    protected getVersionUpload(message: VersionShareFeedbackMessage) : Objects.Version | null {
        return this.uploads.find((v: Objects.Version) => v.id.toString() == message.version_share.options.id_version.toString());
    }
}
